import React from 'react'
import { agnostify } from '@sainsburys-tech/boltui-utils'
import { ComponentHelper } from '../../../helpers/ComponentHelper'
import { M005Props, CategoryNavView } from './types'
import {
  StyledWrapper,
  StyledImageContainer,
  StyledImage,
  StyledBanner,
  StyledContentContainer,
  StyledBodyText
} from './M005.styles'

/**
 ** M005 is an offer card.
 ** The following types are suitable for this component:
 *** - Savings (e.g. Save £50, Save 1/2 Price...)
 ** - Bundle offers (e.g. 3 for 2, 2 for £20...)
 ** - Product offer (e.g. TVs from £89.99, Buy Halo Wars and Get 3 months Xbox Live...)
 ** - Generic deals (e.g. Wow deals, 7 Day Deals)
 ** - Product launch (e.g. new clothing range, new sofa range) - Only if the product launch is not as important as other messages that will be placed the Primary Merch Area, but if you still need to trade (or monetise) that particular product. This is really exceptional.
 */
const componentName = 'm005'

const M005 = ({
  altText,
  categoryNavigationView = CategoryNavView.MOBILE,
  columnProps,
  consumingGrid,
  ctaCopy,
  heading,
  imageZoom = 'contain',
  linkTarget,
  linkUrl,
  linkTitle,
  mainImage,
  subheading,
  offerSize = 0,
  offersLength,
  onLinkClick
}: M005Props): React.ReactElement => {
  const aspectRatio: string = ComponentHelper.dynamicAspectRatio(categoryNavigationView, offerSize)
  return (
    <StyledWrapper
      forwardedAs='a'
      href={agnostify(linkUrl)}
      target={linkTarget}
      title={linkTitle}
      onClick={(event: React.MouseEvent<HTMLElement>) => onLinkClick(event, agnostify(linkUrl))}
      data-test={`${componentName}-container`}
      md={6}
      xs={consumingGrid && offersLength > 1 ? 10 : 12}
      numberOfColumns={12}
      gutterValue={1}
      gutterSize={consumingGrid === 'gl003' || consumingGrid === 'm012' ? '30px' : '15px'}
      {...columnProps}
    >
      <StyledImageContainer data-test={`${componentName}-image-container`}>
        <StyledImage
          src={mainImage}
          templates={['defaultimage=prod-img-placeholder', '$poi$', `sm=aspect&aspect=${aspectRatio}`]}
          quality={50}
          width={540}
          modifier={imageZoom}
          alt={altText}
          data-test={`${componentName}-image-container-image`}
        />
      </StyledImageContainer>
      <StyledBanner data-test={`${componentName}-heading-container`}>
        <p>
          <strong data-test={`${componentName}-heading`}>{heading}</strong>
        </p>
      </StyledBanner>
      <StyledContentContainer data-test={`${componentName}-text-container`}>
        <StyledBodyText className='xs-stack'>
          <p className='no-line-height' style={{ margin: 0 }}>
            <strong data-test={`${componentName}-subheading`}>{subheading}</strong>
          </p>
          <p className='no-line-height' style={{ margin: 0 }}>
            <strong data-test={`${componentName}-ctaCopy`}>{ctaCopy}</strong>
          </p>
        </StyledBodyText>
      </StyledContentContainer>
    </StyledWrapper>
  )
}

export default M005
